import { Tag, TreeSelect, TreeSelectProps } from "antd";
import { ChangeEventExtra, DefaultOptionType, RawValueType } from "rc-tree-select/lib/TreeSelect";
import { CustomTagProps } from "rc-select/lib/BaseSelect";
import React, { useRef } from "react";
import { LabelInValueType } from "rc-select/lib/Select";

/**
 *
 * This enhances the TreeSelect by grouping tags with the same ids
 */
const MultipleTreeSelect = (props: TreeSelectProps) => {
  const uniqueTagsRef = useRef<Map<string, Set<string>>>(new Map());
  const { current: uniqueTags } = uniqueTagsRef;
  const changesRef = useRef<Map<string, string[]>>(new Map());
  const { current: changes } = changesRef;

  const EXTRA = {
    preValue: [],
    triggerValue: "",
    triggerNode: undefined,
    allCheckedNodes: [],
  };

  const handleSelect = (value: RawValueType | LabelInValueType, option: DefaultOptionType) => {
    if (typeof value === "string" && value.includes("¦")) {
      const leaf = value.split("¦")[1];

      const parents = props.treeData
        .flatMap((datum) => datum.children)
        .map((datum) => `${datum.value}`)
        .filter((val) => val.includes(`¦${leaf}`));
      uniqueTags.set(leaf, new Set(parents));

      const change = changes.get(leaf);
      const values = Array.from(new Set(change.concat(parents)));

      props.onChange?.(values, [], EXTRA);
    }

    props.onSelect?.(value, option);
  };

  const handleDeselect = (value: RawValueType | LabelInValueType, option: DefaultOptionType) => {
    if (typeof value === "string" && value.includes("¦")) {
      const leaf = value.split("¦")[1];

      const change = changes.get(leaf);
      uniqueTags.delete(leaf);

      const values = change.filter((val) => !val.includes(`¦${leaf}`));

      props.onChange?.(values, [], EXTRA);
    }

    props.onDeselect?.(value, option);
  };

  const handleChange = (
    value: string[] | RawValueType | LabelInValueType,
    labelList: React.ReactNode[],
    extra: ChangeEventExtra
  ) => {
    const { triggerValue } = extra;
    if (typeof triggerValue === "string" && triggerValue.includes("¦") && Array.isArray(value)) {
      const leaf = triggerValue.split("¦")[1];

      changes.set(leaf, value);
      return;
    }

    return props?.onChange(value, labelList, extra);
  };

  const SingleTagRender = ({ label, ...otherProps }: CustomTagProps) => {
    if (!otherProps?.value?.includes("¦")) return <TreeSelect {...props} treeCheckable />;
    const leaf = otherProps.value.split("¦")[1];

    const wasNewTag = !uniqueTags.has(leaf);
    if (wasNewTag) uniqueTags.set(leaf, new Set());

    const leaves = uniqueTags.get(leaf);
    leaves.add(otherProps.value);

    const isFirstLeaf = wasNewTag || Array.from(leaves)[0] === otherProps.value;
    return isFirstLeaf ? <Tag {...otherProps}>{label}</Tag> : null;
  };

  // we group tags so let's treat each group as one tag in the tag count
  const totalGroupsTags: number = Array.from(uniqueTags.values())
    .filter((set) => set.size > 1)
    .reduce((acc, cur) => acc + (cur.size - 1), 0);
  const maxTagCount = !!props.maxTagCount ? Number(props.maxTagCount) + totalGroupsTags : null;

  return (
    <TreeSelect
      {...props}
      onDeselect={handleDeselect}
      onSelect={handleSelect}
      tagRender={SingleTagRender}
      onChange={handleChange}
      maxTagCount={maxTagCount}
      treeCheckable
    />
  );
};

export default MultipleTreeSelect;
